/**
 * LEOS Mission Ready - Unified Authentication Service
 * Uses Netlify Identity when available, falls back to demo mode
 */

import { netlifyAuth } from './netlify-auth.js';

class UnifiedAuthService {
    constructor() {
        this.currentUser = null;
        this.isAuthenticated = false;
        this.netlifyAuth = netlifyAuth;
        this.identityReady = false;
        
        // Demo account for local development
        this.demoAccount = {
            email: 'kelly.johnson@cyberrts.com',
            password: 'demo123',
            name: 'Kelly Johnson',
            username: 'kjohnson',
            company: 'CyberRTS',
            role: 'Flight Director',
            avatar: 'assets/kelly.svg',
            tier: 'mission-ready',
            team: [
                { name: 'Chris Doe', role: 'Flight Engineer' },
                { name: 'Alex Chen', role: 'Systems Analyst' },
                { name: 'Maya Patel', role: 'Operations Specialist' }
            ],
            missions: { active: 3, completed: 12 },
            satellites: { total: 7, active: 5 }
        };
        
        // Set up event handlers
        this.setupEventHandlers();
        
        // Initialize Netlify Identity when window loads
        this.initializeNetlifyIdentity();
    }
    
    initializeNetlifyIdentity() {
        // Wait for window to load
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.checkNetlifyIdentity();
            });
        } else {
            this.checkNetlifyIdentity();
        }
    }
    
    checkNetlifyIdentity() {
        console.log('Checking for Netlify Identity...');
        
        if (window.netlifyIdentity) {
            console.log('Netlify Identity found, initializing...');
            window.netlifyIdentity.init();
            this.identityReady = true;
            
            // Listen for init event
            window.netlifyIdentity.on('init', () => {
                console.log('Netlify Identity initialized');
                this.identityReady = true;
            });
            
            // Also check if widget is already initialized
            setTimeout(() => {
                if (window.netlifyIdentity) {
                    this.identityReady = true;
                }
            }, 500);
        } else {
            console.log('Netlify Identity not found - running in demo mode');
            // Check again in a moment in case it's still loading
            setTimeout(() => {
                if (window.netlifyIdentity && !this.identityReady) {
                    console.log('Netlify Identity found on retry, initializing...');
                    window.netlifyIdentity.init();
                    this.identityReady = true;
                }
            }, 1000);
        }
    }
    
    setupEventHandlers() {
        // Listen for Netlify Identity events
        this.netlifyAuth.on('onLogin', (user) => {
            this.handleNetlifyLogin(user);
        });
        
        this.netlifyAuth.on('onLogout', () => {
            this.handleLogout();
        });
        
        this.netlifyAuth.on('onSignup', (user) => {
            this.handleNetlifyLogin(user);
        });
    }
    
    handleNetlifyLogin(user) {
        const userInfo = this.netlifyAuth.getUserInfo();
        
        this.currentUser = {
            ...userInfo,
            ...this.demoAccount, // Merge demo data for UI completeness
            email: userInfo.email,
            name: userInfo.name,
            id: userInfo.id
        };
        
        this.isAuthenticated = true;
        
        // Add session expiration (4 hours)
        const sessionData = {
            user: this.currentUser,
            expiresAt: Date.now() + (4 * 60 * 60 * 1000) // 4 hours
        };
        
        localStorage.setItem('leos-user', JSON.stringify(sessionData));
        localStorage.setItem('auth-mode', 'netlify');
    }
    
    handleLogout() {
        this.currentUser = null;
        this.isAuthenticated = false;
        localStorage.removeItem('leos-user');
        localStorage.removeItem('auth-mode');
    }
    
    /**
     * Check for existing session without initializing
     */
    checkExistingSession() {
        // Check for existing session
        const storedData = localStorage.getItem('leos-user');
        const authMode = localStorage.getItem('auth-mode');
        
        if (storedData) {
            try {
                const sessionData = JSON.parse(storedData);
                
                // Check if it's the new format with expiration
                if (sessionData.expiresAt) {
                    // Check if session has expired
                    if (Date.now() > sessionData.expiresAt) {
                        console.log('Session expired, clearing...');
                        localStorage.removeItem('leos-user');
                        localStorage.removeItem('auth-mode');
                        return null;
                    }
                    
                    // Session is valid
                    const user = sessionData.user;
                    
                    // If using Netlify auth, verify token is still valid
                    if (authMode === 'netlify' && this.netlifyAuth.isAuthenticated()) {
                        this.currentUser = user;
                        this.isAuthenticated = true;
                        return user;
                    } else if (authMode === 'demo') {
                        this.currentUser = user;
                        this.isAuthenticated = true;
                        return user;
                    }
                } else {
                    // Old format, treat as user object directly (for backward compatibility)
                    const user = sessionData;
                    
                    if (authMode === 'netlify' && this.netlifyAuth.isAuthenticated()) {
                        this.currentUser = user;
                        this.isAuthenticated = true;
                        return user;
                    } else if (authMode === 'demo') {
                        this.currentUser = user;
                        this.isAuthenticated = true;
                        return user;
                    }
                }
            } catch (error) {
                localStorage.removeItem('leos-user');
                localStorage.removeItem('auth-mode');
            }
        }
        
        // Check if Netlify Identity has a user
        if (this.netlifyAuth.isAuthenticated()) {
            const netlifyUser = this.netlifyAuth.getCurrentUser();
            if (netlifyUser) {
                this.handleNetlifyLogin(netlifyUser);
                return this.currentUser;
            }
        }
        
        return null;
    }
    
    /**
     * Initialize authentication system
     */
    init() {
        // Use the checkExistingSession method which handles expiration
        const existingUser = this.checkExistingSession();
        
        if (existingUser) {
            return true;
        }
        
        return false;
    }
    
    /**
     * Login method - tries Netlify first, falls back to demo
     */
    async login(email, password) {
        // If no parameters, open Netlify Identity widget
        if (!email && !password) {
            return this.openNetlifyLogin();
        }
        
        // Fall back to demo mode with credentials
        if (email === this.demoAccount.email && password === this.demoAccount.password) {
            this.currentUser = { ...this.demoAccount };
            this.isAuthenticated = true;
            
            // Add session expiration (4 hours for demo)
            const sessionData = {
                user: this.currentUser,
                expiresAt: Date.now() + (4 * 60 * 60 * 1000) // 4 hours
            };
            
            localStorage.setItem('leos-user', JSON.stringify(sessionData));
            localStorage.setItem('auth-mode', 'demo');
            return { success: true, user: this.currentUser, mode: 'demo' };
        }
        
        return { success: false, error: 'Invalid credentials' };
    }
    
    /**
     * Open Netlify Identity login widget
     */
    openNetlifyLogin() {
        console.log('Opening Netlify Identity login...');
        console.log('Netlify Identity available:', !!window.netlifyIdentity);
        console.log('Identity ready:', this.identityReady);
        
        if (window.netlifyIdentity) {
            try {
                window.netlifyIdentity.open('login');
                return { success: true, mode: 'netlify' };
            } catch (error) {
                console.error('Error opening login:', error);
                // Try initializing again
                window.netlifyIdentity.init();
                setTimeout(() => {
                    try {
                        window.netlifyIdentity.open('login');
                    } catch (e) {
                        console.error('Failed to open login after init:', e);
                    }
                }, 200);
                return { success: true, mode: 'netlify' };
            }
        } else {
            console.warn('Netlify Identity not available');
            this.showError('Authentication system not available. Make sure you are running with ./run-dev.sh');
            return { success: false, error: 'Netlify Identity not available' };
        }
    }
    
    /**
     * Signup method - opens Netlify Identity signup
     */
    signup() {
        console.log('Opening signup...');
        
        // Always show custom signup modal for consistent experience
        this.showSignupModal();
    }
    
    showSignupModal() {
        // Create signup modal
        const modal = document.createElement('div');
        modal.className = 'signup-modal';
        modal.innerHTML = `
            <div class="signup-backdrop" onclick="document.querySelector('.signup-modal').remove()"></div>
            <div class="signup-container">
                <div class="signup-header">
                    <h2>Create Mission Ready Account</h2>
                    <button class="close-btn" onclick="document.querySelector('.signup-modal').remove()">×</button>
                </div>
                
                <div class="signup-content">
                    <div class="trial-banner">
                        <img src="assets/rocket.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                        <span>Start your 7-day free trial</span>
                    </div>
                    
                    <form id="signup-form" onsubmit="return false;">
                        <div class="form-group">
                            <label>Full Name</label>
                            <input type="text" id="signup-name" required placeholder="John Doe" />
                        </div>
                        
                        <div class="form-group">
                            <label>Email Address</label>
                            <input type="email" id="signup-email" required placeholder="john@company.com" />
                        </div>
                        
                        <div class="form-group">
                            <label>Organization</label>
                            <input type="text" id="signup-org" required placeholder="Acme Aerospace" />
                        </div>
                        
                        <div class="form-group">
                            <label>Password</label>
                            <input type="password" id="signup-password" required placeholder="••••••••" />
                        </div>
                        
                        <div class="form-group">
                            <label>Confirm Password</label>
                            <input type="password" id="signup-confirm" required placeholder="••••••••" />
                        </div>
                        
                        <div class="pricing-info">
                            <h3>Mission Ready Features</h3>
                            <ul>
                                <li>✓ Full satellite fleet management</li>
                                <li>✓ Real-time telemetry dashboard</li>
                                <li>✓ Ground station network control</li>
                                <li>✓ Space domain awareness</li>
                                <li>✓ Mission planning tools</li>
                                <li>✓ Team collaboration (up to 5 users)</li>
                            </ul>
                            <div class="price-tag">
                                <span class="price">$99.99</span>
                                <span class="period">/month after trial</span>
                            </div>
                        </div>
                        
                        <button type="submit" class="signup-btn" onclick="window.unifiedAuth.handleSignupSubmit()">
                            Start Free Trial
                        </button>
                        
                        <p class="terms">
                            By creating an account, you agree to our <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>
                        </p>
                    </form>
                </div>
            </div>
        `;
        
        // Add styles if not already present
        if (!document.querySelector('#signup-modal-styles')) {
            const style = document.createElement('style');
            style.id = 'signup-modal-styles';
            style.textContent = `
                .signup-modal {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    z-index: 10000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .signup-backdrop {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.8);
                    backdrop-filter: blur(5px);
                }
                
                .signup-container {
                    position: relative;
                    background: rgba(15, 23, 42, 0.95);
                    border: 1px solid rgba(0, 212, 255, 0.3);
                    border-radius: 16px;
                    width: 90%;
                    max-width: 500px;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 0 40px rgba(0, 212, 255, 0.2);
                }
                
                .signup-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 24px;
                    border-bottom: 1px solid rgba(148, 163, 184, 0.1);
                }
                
                .signup-header h2 {
                    margin: 0;
                    color: #e2e8f0;
                    font-size: 24px;
                    font-weight: 600;
                }
                
                .close-btn {
                    background: none;
                    border: none;
                    color: #64748b;
                    font-size: 28px;
                    cursor: pointer;
                    padding: 0;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    transition: all 0.2s;
                }
                
                .close-btn:hover {
                    background: rgba(148, 163, 184, 0.1);
                    color: #e2e8f0;
                }
                
                .signup-content {
                    padding: 24px;
                }
                
                .trial-banner {
                    background: rgba(16, 185, 129, 0.1);
                    border: 1px solid rgba(16, 185, 129, 0.3);
                    border-radius: 8px;
                    padding: 12px 16px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 24px;
                    color: #10b981;
                    font-weight: 500;
                }
                
                .form-group {
                    margin-bottom: 20px;
                }
                
                .form-group label {
                    display: block;
                    color: #94a3b8;
                    font-size: 14px;
                    font-weight: 500;
                    margin-bottom: 8px;
                }
                
                .form-group input {
                    width: 100%;
                    padding: 12px 16px;
                    background: rgba(30, 41, 59, 0.5);
                    border: 1px solid rgba(148, 163, 184, 0.2);
                    border-radius: 8px;
                    color: #e2e8f0;
                    font-size: 14px;
                    transition: all 0.2s;
                    box-sizing: border-box;
                }
                
                .form-group input:focus {
                    outline: none;
                    border-color: #00d4ff;
                    background: rgba(30, 41, 59, 0.8);
                }
                
                .pricing-info {
                    background: rgba(30, 41, 59, 0.3);
                    border: 1px solid rgba(148, 163, 184, 0.1);
                    border-radius: 12px;
                    padding: 20px;
                    margin: 24px 0;
                }
                
                .pricing-info h3 {
                    color: #e2e8f0;
                    font-size: 18px;
                    margin: 0 0 16px 0;
                }
                
                .pricing-info ul {
                    list-style: none;
                    padding: 0;
                    margin: 0 0 20px 0;
                }
                
                .pricing-info li {
                    color: #94a3b8;
                    padding: 8px 0;
                    font-size: 14px;
                }
                
                .price-tag {
                    text-align: center;
                    padding-top: 16px;
                    border-top: 1px solid rgba(148, 163, 184, 0.1);
                }
                
                .price {
                    color: #00d4ff;
                    font-size: 32px;
                    font-weight: 700;
                }
                
                .period {
                    color: #64748b;
                    font-size: 14px;
                    margin-left: 8px;
                }
                
                .signup-btn {
                    width: 100%;
                    padding: 14px;
                    background: linear-gradient(135deg, #00d4ff 0%, #0099ff 100%);
                    border: none;
                    border-radius: 8px;
                    color: white;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                
                .signup-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 20px rgba(0, 212, 255, 0.4);
                }
                
                .terms {
                    text-align: center;
                    color: #64748b;
                    font-size: 12px;
                    margin-top: 16px;
                }
                
                .terms a {
                    color: #00d4ff;
                    text-decoration: none;
                }
                
                .terms a:hover {
                    text-decoration: underline;
                }
            `;
            
            document.head.appendChild(style);
        }
        
        document.body.appendChild(modal);
        
        // Focus first input
        setTimeout(() => {
            document.getElementById('signup-name')?.focus();
        }, 100);
    }
    
    handleSignupSubmit() {
        const name = document.getElementById('signup-name').value;
        const email = document.getElementById('signup-email').value;
        const org = document.getElementById('signup-org').value;
        const password = document.getElementById('signup-password').value;
        const confirm = document.getElementById('signup-confirm').value;
        
        // Validation
        if (password !== confirm) {
            alert('Passwords do not match');
            return;
        }
        
        if (password.length < 8) {
            alert('Password must be at least 8 characters');
            return;
        }
        
        // In demo mode, just show success and login
        console.log('Demo signup:', { name, email, org });
        
        // Show success message
        alert('Account created successfully! You can now login with your credentials.\n\nDemo mode: Use the demo account to continue.');
        
        // Close modal
        document.querySelector('.signup-modal')?.remove();
        
        // Could auto-fill demo credentials here if desired
    }
    
    /**
     * SSO Login method - uses Netlify Identity with Google provider
     */
    loginSSO() {
        console.log('Opening SSO login...');
        
        if (window.netlifyIdentity) {
            try {
                // Open Netlify Identity with login mode
                window.netlifyIdentity.open('login');
                // Note: Google SSO needs to be configured in Netlify dashboard
            } catch (error) {
                console.error('Error opening SSO login:', error);
                // Try initializing again
                window.netlifyIdentity.init();
                setTimeout(() => {
                    try {
                        window.netlifyIdentity.open('login');
                    } catch (e) {
                        console.error('Failed to open SSO login after init:', e);
                        this.showError('SSO login requires Netlify Identity to be configured');
                    }
                }, 200);
            }
        } else {
            this.showError('SSO requires running with backend (./run-dev.sh). Currently in demo mode.');
        }
    }
    
    /**
     * Show login screen - restored original beautiful interface
     */
    showLoginScreen() {
        console.log('UnifiedAuth.showLoginScreen() called');
        
        // Hide time display immediately
        const timeDisplay = document.getElementById('time-display');
        if (timeDisplay) {
            timeDisplay.style.display = 'none';
        }
        const timeDisplayContainer = document.getElementById('time-display-container');
        if (timeDisplayContainer) {
            timeDisplayContainer.style.display = 'none';
        }
        
        const loadingScreen = document.getElementById('loading-screen');
        if (!loadingScreen) {
            console.error('Loading screen not found');
            return;
        }
        
        // Make sure loading screen is visible
        loadingScreen.style.display = 'flex';
        loadingScreen.style.opacity = '1';
        loadingScreen.style.visibility = 'visible';
        loadingScreen.style.zIndex = '10001';
        
        // Use setProperty to force important if needed
        loadingScreen.style.setProperty('display', 'flex', 'important');
        
        // Clear and set all content at once
        loadingScreen.innerHTML = `
            <!-- Aurora Background Canvas -->
            <canvas id="aurora-canvas" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 0;"></canvas>
            <!-- Professional Login Interface -->
            <div class="login-interface" style="background: transparent;">
                <!-- Top Branding -->
                <div class="login-header">
                    <a href="https://www.cyberrts.com/" target="_blank" class="cyberrts-logo-link">
                        <img src="assets/CyberRTS-Full-Logo.svg" alt="CyberRTS" class="cyberrts-logo" />
                    </a>
                    <div class="leos-branding">
                        <h1 class="leos-title"><span class="leos-highlight">LEOS</span>: Mission Ready</h1>
                    </div>
                </div>
                
                <!-- Main Login Card -->
                <div class="login-card">
                    <div class="login-card-header">
                        <h2>Sign In to Mission Control</h2>
                        <p>Access your space operations dashboard</p>
                    </div>
                    
                    <!-- Netlify Identity Login -->
                    <button class="sso-btn" onclick="window.unifiedAuth.loginSSO()">
                        <img src="assets/CyberRTS-Simple-Logo.svg" alt="CyberRTS" style="width: 20px; height: 20px; margin-right: 8px;" />
                        Sign in with SSO (Google)
                    </button>
                    
                    <div class="divider">
                        <span>or</span>
                    </div>
                    
                    <form id="login-form" class="login-form">
                        <div class="form-group">
                            <label for="email">Email Address</label>
                            <input type="email" id="email" placeholder="your.email@company.com" required />
                        </div>
                        
                        <div class="form-group">
                            <label for="password">Password</label>
                            <input type="password" id="password" placeholder="Enter your password" required />
                        </div>
                        
                        <button type="submit" class="login-btn">
                            <span>Launch Mission Ready</span>
                        </button>
                        
                        <div class="auth-error" id="auth-error" style="display: none;"></div>
                    </form>
                    
                    <!-- Demo Accounts Section -->
                    <div class="demo-accounts-section">
                        <h3>Demo Accounts</h3>
                        <div class="demo-accounts-grid">
                            <div class="demo-account-card" onclick="window.unifiedAuth.fillDemoCredentials('kelly.johnson@cyberrts.com', 'demo123')">
                                <div class="demo-avatar">
                                    <img src="assets/kelly.svg" alt="Kelly" />
                                </div>
                                <div class="demo-info">
                                    <h4>Kelly Johnson</h4>
                                    <p>Flight Director • kjohnson@cyberrts.com</p>
                                </div>
                                <div class="demo-tier mission-ready">ENTERPRISE</div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Footer -->
                <div class="login-footer">
                    <p>New to LEOS? <a href="#" onclick="window.unifiedAuth.signup(); return false;">Create a Mission Ready Account</a></p>
                    <p class="footer-secondary">Enterprise customers: <a href="https://www.cyberrts.com/" target="_blank">Contact CyberRTS</a></p>
                </div>
            </div>
        `;
        
        // Restart the aurora background
        this.initializeAuroraBackground();
        this.attachLoginHandlers();
        
        // Ensure login interface is visible
        const loginInterface = loadingScreen.querySelector('.login-interface');
        if (loginInterface) {
            loginInterface.style.display = 'flex';
            loginInterface.style.opacity = '1';
            loginInterface.style.position = 'relative';
            loginInterface.style.zIndex = '1002';
        }
        
        console.log('Login screen content set, aurora initialized');
    }
    
    /**
     * Fill demo credentials
     */
    fillDemoCredentials(email, password) {
        const emailInput = document.getElementById('email');
        const passwordInput = document.getElementById('password');
        if (emailInput && passwordInput) {
            emailInput.value = email;
            passwordInput.value = password;
        }
    }
    
    /**
     * Attach login form handlers
     */
    attachLoginHandlers() {
        const form = document.getElementById('login-form');
        if (form) {
            form.addEventListener('submit', async (e) => {
                e.preventDefault();
                
                const email = document.getElementById('email').value;
                const password = document.getElementById('password').value;
                
                const result = await this.login(email, password);
                
                if (result.success) {
                    this.hideLoginScreen();
                    this.showWelcomeFlow();
                } else {
                    this.showError(result.error || 'Invalid credentials. Please use one of the demo accounts.');
                }
            });
        }
    }
    
    /**
     * Show personalized welcome flow
     */
    showWelcomeFlow() {
        // Create a simple loading overlay
        const loadingScreen = document.createElement('div');
        loadingScreen.id = 'loading-overlay';
        loadingScreen.className = 'loading-overlay';
        
        loadingScreen.innerHTML = `
            <div class="loading-content">
                <img src="assets/CyberRTS-Simple-Logo.svg" alt="CyberRTS" class="loading-logo" />
                <div class="loading-text">Initializing Mission Ready</div>
                <div class="loading-progress">
                    <div class="progress-bar">
                        <div class="progress-fill"></div>
                    </div>
                    <div class="loading-status">Connecting to satellites...</div>
                </div>
            </div>
        `;
        
        document.body.appendChild(loadingScreen);
        
        // Hide the old time display immediately
        const timeDisplay = document.getElementById('time-display');
        if (timeDisplay) {
            timeDisplay.style.display = 'none';
        }
        
        // Start loading animation
        this.animateLoading();
        
        // Immediately trigger Mission Ready initialization
        if (window.onAuthComplete) {
            window.onAuthComplete(this.currentUser);
        }
        
        // Don't auto-remove - let the simulation loading complete first
    }
    
    /**
     * Animate initialization status
     */
    animateLoading() {
        const progressFill = document.querySelector('.progress-fill');
        const loadingStatus = document.querySelector('.loading-status');
        
        const statuses = [
            'Initializing Babylon.js engine...',
            'Loading Earth textures...',
            'Generating atmosphere shaders...',
            'Loading satellite data...',
            'Computing orbital elements...',
            'Initializing ground stations...',
            'Establishing telemetry links...',
            'Loading Moon textures...',
            'Calculating ephemeris data...',
            'Preparing 3D visualization...',
            'Finalizing mission parameters...',
            'Mission Ready'
        ];
        
        let progress = 0;
        let statusIndex = 0;
        
        // Slower, more realistic loading
        const interval = setInterval(() => {
            // Variable progress increments for more realistic feel
            const increment = Math.random() * 15 + 5; // 5-20% increment
            progress = Math.min(progress + increment, 95); // Cap at 95% until actually ready
            
            if (progressFill) {
                progressFill.style.width = `${progress}%`;
            }
            
            // Update status based on progress
            const newStatusIndex = Math.floor((progress / 100) * (statuses.length - 1));
            if (newStatusIndex !== statusIndex && loadingStatus) {
                statusIndex = newStatusIndex;
                loadingStatus.textContent = statuses[statusIndex];
            }
            
            // Stop at 95% and wait for actual completion
            if (progress >= 95) {
                clearInterval(interval);
                // Final progress will be set by app-mission-ready.js
                window.completeLoading = () => {
                    if (progressFill) progressFill.style.width = '100%';
                    if (loadingStatus) loadingStatus.textContent = 'Mission Ready';
                };
            }
        }, 800); // Slower updates for more realistic feel
    }
    
    /**
     * Initialize aurora background
     */
    initializeAuroraBackground() {
        import('../aurora-background.js').then(module => {
            console.log('Aurora background module loaded');
            if (module.initAuroraBackground) {
                module.initAuroraBackground();
                console.log('Aurora background initialized');
            }
        }).catch(error => {
            console.error('Aurora background error:', error);
        });
    }
    
    /**
     * Hide login screen
     */
    hideLoginScreen() {
        const loadingScreen = document.getElementById('loading-screen');
        if (loadingScreen) {
            loadingScreen.style.opacity = '0';
            setTimeout(() => {
                loadingScreen.style.display = 'none';
                // Set authenticated flag so app.js knows it's safe to proceed
                window.isAuthenticated = true;
            }, 500);
        }
    }
    
    /**
     * Show authentication error
     */
    showError(message) {
        const errorEl = document.getElementById('auth-error');
        if (errorEl) {
            errorEl.textContent = message;
            errorEl.style.display = 'block';
        }
    }

    /**
     * Logout method
     */
    logout() {
        if (localStorage.getItem('auth-mode') === 'netlify') {
            this.netlifyAuth.logout();
        } else {
            this.handleLogout();
        }
    }
    
    /**
     * Get authentication state
     */
    isLoggedIn() {
        return this.isAuthenticated;
    }
    
    /**
     * Get current user
     */
    getUser() {
        return this.currentUser;
    }
    
    /**
     * API helper method
     */
    async callAPI(endpoint, options = {}) {
        const authMode = localStorage.getItem('auth-mode');
        
        if (authMode === 'netlify' && this.netlifyAuth.getToken()) {
            // Use real API with Netlify auth
            return this.netlifyAuth.callAPI(endpoint, options);
        } else {
            // Return mock data for demo mode
            return this.getMockAPIResponse(endpoint, options);
        }
    }
    
    /**
     * Mock API responses for demo mode
     */
    async getMockAPIResponse(endpoint, options) {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Mock responses based on endpoint
        if (endpoint.includes('/satellites')) {
            return {
                satellites: [
                    { id: 'sat-1', name: 'CRTS-1', noradId: '00001', status: 'active' },
                    { id: 'sat-2', name: 'BULLDOG', noradId: '00002', status: 'active' }
                ],
                count: 2,
                limit: this.currentUser?.tier === 'mission-ready' ? 100 : 3
            };
        }
        
        if (endpoint.includes('/telemetry')) {
            return {
                telemetry: [
                    {
                        timestamp: new Date().toISOString(),
                        position: { x: 7000, y: 0, z: 0 },
                        battery: 95,
                        temperature: 22
                    }
                ]
            };
        }
        
        if (endpoint.includes('/auth/profile')) {
            return { user: this.currentUser };
        }
        
        return { message: 'Mock response' };
    }
}

// Create singleton instance
export const unifiedAuth = new UnifiedAuthService();

// Make it globally available for onclick handlers
window.unifiedAuth = unifiedAuth;

// Also export as simpleAuth for backward compatibility
export const simpleAuth = unifiedAuth;